import {useLayoutEffect, useRef, useState} from "react";
import {Box, Chip, Popover, Typography} from "@mui/material";

const CHIP_GAP = 6;

const roleLabel = (role) => `${role.name}: ${role.count}`;

/**
 * Muestra los roles vinculados en una sola línea dentro de la altura de la fila.
 * Los que no caben se sustituyen por un chip "+N" que abre la lista completa.
 */
const LinkedRolesChips = ({roles}) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [chipWidths, setChipWidths] = useState([]);
  const [moreWidth, setMoreWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const total = roles.length;
  const rolesKey = roles.map(roleLabel).join("|");

  // Mide cada chip sin recortar. Se repite solo cuando cambian los roles.
  useLayoutEffect(() => {
    const widths = Array.from(measureRef.current?.children ?? [], (el) => el.offsetWidth);
    setChipWidths(widths.slice(0, total));
    setMoreWidth(widths[total] ?? 0);
  }, [rolesKey, total]);

  // Recalcula al cambiar el ancho de la celda (redimensión de columna o ventana).
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const update = () => setContainerWidth(element.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const widthOf = (count) => {
    const widths = chipWidths.slice(0, count);
    if (count < total) widths.push(moreWidth);
    return widths.reduce((sum, width) => sum + width, 0) + CHIP_GAP * Math.max(widths.length - 1, 0);
  };

  let visibleCount = total;
  if (containerWidth > 0) {
    while (visibleCount > 0 && widthOf(visibleCount) > containerWidth) visibleCount--;
  }
  const hiddenCount = total - visibleCount;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: `${CHIP_GAP}px`,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        flexWrap: "nowrap"
      }}
    >
      {roles.slice(0, visibleCount).map((role) => (
        <Chip key={role.name} label={roleLabel(role)} size="small" variant="outlined" />
      ))}

      {hiddenCount > 0 && (
        <Chip
          label={`+${hiddenCount}`}
          size="small"
          color="primary"
          variant="outlined"
          clickable
          aria-label={`Ver los ${total} roles vinculados`}
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        />
      )}

      {/* Copia oculta solo para medir el ancho natural de cada chip. */}
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          visibility: "hidden",
          pointerEvents: "none",
          display: "flex",
          gap: `${CHIP_GAP}px`,
          whiteSpace: "nowrap"
        }}
      >
        {roles.map((role) => (
          <Chip key={role.name} label={roleLabel(role)} size="small" variant="outlined" />
        ))}
        <Chip label={`+${total}`} size="small" variant="outlined" />
      </Box>

      {/* Se corta la propagación para que el clic no abra la fila del tenant. */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
      >
        <Box sx={{p: 2, maxWidth: 320}}>
          <Typography variant="subtitle2" sx={{mb: 1}}>
            Roles vinculados
          </Typography>
          <Box sx={{display: "flex", flexWrap: "wrap", gap: `${CHIP_GAP}px`}}>
            {roles.map((role) => (
              <Chip key={role.name} label={roleLabel(role)} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default LinkedRolesChips;
